import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { VoiceIntent, VoiceMessageOption } from '../models';
import { ProjectService } from '../../../modules/project-management/services/project.service';
import { VoiceSessionStoreService } from './voice-session-store.service';

/** Clarification en attente : l'intent original, mémorisé pour être rejoué une fois le projet choisi */
export interface PendingClarification {
  intent: VoiceIntent;
}

/**
 * Service minimal dédié à la clarification "projet manquant" (MOKA-206 Phase 3,
 * Lot C). Extrait à dessein de `VoiceGuidedFlowService` : `VoiceGuidedFlowService`
 * injecte `VoiceService` (pour exécuter l'intent final, point d'entrée unique —
 * cf. `VoiceService.executeIntent`) et `VoiceService` a besoin, symétriquement,
 * de construire les options de clarification et de mémoriser l'intent en
 * attente. Si ces deux responsabilités vivaient sur `VoiceGuidedFlowService`,
 * `VoiceService` devrait l'injecter en retour → cycle DI classique (deux
 * services `providedIn: 'root'` s'injectant mutuellement via `inject()` lèvent
 * `NG0200: Circular dependency in DI detected` dès la construction du premier).
 * Ce troisième service, sans dépendance vers `VoiceService` ni
 * `VoiceGuidedFlowService`, casse le cycle : `VoiceService` l'injecte pour
 * poser la clarification, `VoiceGuidedFlowService` l'injecte pour la résoudre
 * (déléguer vers `VoiceService.executeIntent`) — la flèche ne va jamais que
 * dans un sens vers ce service.
 */
@Injectable({ providedIn: 'root' })
export class VoiceClarificationService {
  private sessionStore = inject(VoiceSessionStoreService);
  private projectService = inject(ProjectService);

  #pending = signal<PendingClarification | null>(null);

  /** Clarification "projet manquant" en attente d'un choix utilisateur (ou `null`) */
  pendingClarification = computed(() => this.#pending());

  /**
   * Construit les options de clarification : tous les projets accessibles,
   * `label` = nom du projet, `id` = identifiant court (réutilisable tel quel
   * comme `params.projectName` par `resolveProjectId` côté handler — qui sait
   * résoudre un identifiant via `getProjectByIdentifier`).
   */
  async buildProjectOptions(): Promise<VoiceMessageOption[]> {
    const response = await firstValueFrom(this.projectService.getAllProjects());
    const options = response.projects.map(p => ({ id: p.identifier, label: p.name }));

    // MOKA-208 P4 : le projet le plus fréquent dans les conversations passées
    // remonte en tête. Il est SUGGÉRÉ, pas choisi : la liste complète reste
    // affichée et l'ordre est le seul indice donné.
    try {
      const frequent = await this.sessionStore.getFrequentProjectIdentifier();
      if (!frequent) return options;

      const index = options.findIndex(o => o.id === frequent);
      if (index > 0) {
        const [preferred] = options.splice(index, 1);
        options.unshift(preferred);
      }
    } catch {
      // Une préférence indisponible ne doit pas empêcher de choisir un projet.
    }

    return options;
  }

  /** Mémorise l'intent dont l'exécution est suspendue en attente du choix de projet */
  setPendingClarification(intent: VoiceIntent): void {
    this.#pending.set({ intent });
  }

  /** Efface la clarification en attente sans l'exécuter (annulation, cf. règle F appliquée au Lot C) */
  clearPendingClarification(): void {
    this.#pending.set(null);
  }
}
